import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import CKEditor from "react-ckeditor-component";
import { useDispatch } from "react-redux";
import {
  approveBlog,
  deleteBlog,
  getBlogCategories,
  getBlogs,
  rejectBlog,
  saveBlog,
} from "../../../store/actions/councilActions/blogAction";
import { errorAlert } from "../../../utils/alert";
import Paging from "../../Common/Paging";

/*
  Admin: blogs, category wise.

  News is a separate thing and is untouched. A post is written here and shows
  on the website as soon as it is active; switching it off takes it down
  without deleting it.
*/
const dmy = (value) => (value ? moment(value).format("DD/MM/YYYY") : "--");
const dmyhm = (value) =>
  value ? moment(value).format("DD/MM/YYYY hh:mm A") : "--";

/* Set for later than now means it has not gone live yet. */
const isScheduled = (row) =>
  Number(row.is_active) === 1 &&
  row.published_at &&
  moment(row.published_at).isAfter(moment());

/* A post can be marked as a daily, weekly or monthly one - the website
   shows the label and lets a reader filter on it. */
const FREQUENCIES = [
  { key: "", label: "Not set" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const EMPTY = {
  title: "",
  category_id: "",
  frequency: "",
  short_description: "",
  description: "",
  author: "",
  video_url: "",
  published_at: "",
  is_active: 1,
};

const BlogList = () => {
  const dispatch = useDispatch();

  const [list, setList] = useState(null);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(null); // null = closed
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await dispatch(getBlogs({ category, search, page }));
    if (data) setList(data);
    setLoading(false);
  }, [dispatch, category, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    dispatch(getBlogCategories(false)).then(setCategories);
  }, [dispatch]);

  const openNew = () => {
    setForm({ ...EMPTY, published_at: moment().format("YYYY-MM-DDTHH:mm") });
    setFile(null);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      category_id: row.category_id || "",
      frequency: row.frequency || "",
      short_description: row.short_description || "",
      description: row.description || "",
      author: row.author || "",
      video_url: row.video_url || "",
      // "2026-09-01 18:30:00" from the server, "2026-09-01T18:30" for the
      // input, which is the only shape a datetime-local box accepts.
      published_at: row.published_at
        ? String(row.published_at).replace(" ", "T").slice(0, 16)
        : "",
      is_active: Number(row.is_active),
    });
    setFile(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) return errorAlert("Please write a title.");
    if (!String(form.description).trim())
      return errorAlert("Please write the blog content.");

    setBusy(true);
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== "id") body.append(key, value === null ? "" : value);
    });
    if (file) body.append("file", file);

    const done = await dispatch(saveBlog(body, form.id));
    setBusy(false);

    if (done) {
      setForm(null);
      load();
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    const done = await dispatch(deleteBlog(row.id));
    if (done) load();
  };

  const approve = async (row) => {
    if (!window.confirm(`Approve and publish "${row.title}"?`)) return;
    const done = await dispatch(approveBlog(row.id));
    if (done) load();
  };

  const reject = async (row) => {
    const reason = window.prompt(`Reason for rejecting "${row.title}":`, "");
    if (reason === null) return;
    if (!reason.trim()) return errorAlert("A reason is required to reject.");
    const done = await dispatch(rejectBlog(row.id, reason.trim()));
    if (done) load();
  };

  // Approval word + colour. A blog a centre submitted waits at 0 (pending);
  // the council's own posts and approved ones are 1.
  const approval = (row) => {
    if (Number(row.approval_status) === 2)
      return { label: "Rejected", cls: "text-danger" };
    if (Number(row.approval_status) === 0)
      return { label: "Pending", cls: "text-warning" };
    return { label: "Approved", cls: "text-success" };
  };

  const pagination = list
    ? {
        page: Number(list.current_page),
        pages: Number(list.last_page),
        total: Number(list.total),
        per_page: Number(list.per_page),
      }
    : null;

  const rows = list?.data || [];

  return (
    <div>
      <div class="d-flex justify-content-between align-items-center">
        <div class="BigTitle">Blogs</div>
        <button type="button" class="btn btn-comman" onClick={openNew}>
          Add Blog
        </button>
      </div>

      <div class="row mt-3 mb-2">
        <div class="col-md-4 mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Search by title"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div class="col-md-4 mb-2">
          <select
            class="form-control"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option value={c.id} key={c.id}>
                {c.name} ({c.blogs_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Image</th>
              <th>Title</th>
              <th>Category</th>
              <th>Type</th>
              <th>Author</th>
              <th>Published</th>
              <th>Views</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11}>Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11}>No blog found.</td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={row.id}>
                  <td>{(pagination.page - 1) * pagination.per_page + i + 1}</td>
                  <td>
                    {row.file ? (
                      <img src={row.file} alt={row.title} height="40" />
                    ) : (
                      "--"
                    )}
                  </td>
                  <td>{row.title}</td>
                  <td>{row.category?.name || "--"}</td>
                  <td class="text-capitalize">{row.frequency || "--"}</td>
                  <td>{row.author || "--"}</td>
                  <td>{dmyhm(row.published_at)}</td>
                  <td>{Number(row.views) || 0}</td>
                  <td>
                    {Number(row.is_active) !== 1 ? (
                      <span class="text-muted">Draft</span>
                    ) : isScheduled(row) ? (
                      <span class="text-warning">Scheduled</span>
                    ) : (
                      <span class="text-success">Live</span>
                    )}
                  </td>
                  <td>
                    <span class={approval(row).cls}>{approval(row).label}</span>
                    {Number(row.approval_status) === 2 && row.reject_reason && (
                      <div
                        style={{ fontSize: 11, color: "#999" }}
                        title={row.reject_reason}
                      >
                        {row.reject_reason}
                      </div>
                    )}
                  </td>
                  <td>
                    {Number(row.approval_status) !== 1 && (
                      <button
                        type="button"
                        class="btn btn-sm btn-success mr-1 mb-1"
                        onClick={() => approve(row)}
                      >
                        Approve
                      </button>
                    )}
                    {Number(row.approval_status) === 0 && (
                      <button
                        type="button"
                        class="btn btn-sm btn-warning mr-1 mb-1"
                        onClick={() => reject(row)}
                      >
                        Reject
                      </button>
                    )}
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary mr-1 mb-1"
                      onClick={() => openEdit(row)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger mb-1"
                      onClick={() => remove(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Paging pagination={pagination} onPage={setPage} />

      <Modal show={!!form} onHide={() => setForm(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{form?.id ? "Edit Blog" : "Add Blog"}</Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submit}>
            <Modal.Body>
              <div class="row">
                <div class="col-md-8 mb-2">
                  <label>Title *</label>
                  <input
                    type="text"
                    class="form-control"
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                    required
                  />
                </div>
                <div class="col-md-4 mb-2">
                  <label>Category</label>
                  <select
                    class="form-control"
                    value={form.category_id}
                    onChange={(e) =>
                      setForm({ ...form, category_id: e.target.value })
                    }
                  >
                    <option value="">-- none --</option>
                    {categories.map((c) => (
                      <option value={c.id} key={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Blog type</label>
                  <select
                    class="form-control"
                    value={form.frequency}
                    onChange={(e) =>
                      setForm({ ...form, frequency: e.target.value })
                    }
                  >
                    {FREQUENCIES.map((f) => (
                      <option value={f.key} key={f.key}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Author</label>
                  <input
                    type="text"
                    class="form-control"
                    value={form.author}
                    onChange={(e) =>
                      setForm({ ...form, author: e.target.value })
                    }
                  />
                </div>
                <div class="col-md-4 mb-2">
                  <label>Publish date &amp; time</label>
                  <input
                    type="datetime-local"
                    class="form-control"
                    value={form.published_at}
                    onChange={(e) =>
                      setForm({ ...form, published_at: e.target.value })
                    }
                  />
                  {/* Nothing has to be done at that moment: the website
                      simply does not show the post until it arrives. */}
                  <small class="text-muted">
                    Put a time in the future and the blog publishes itself
                    then. Status must be "Live on the website".
                  </small>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Status</label>
                  <select
                    class="form-control"
                    value={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: Number(e.target.value) })
                    }
                  >
                    <option value={1}>Live on the website</option>
                    <option value={0}>Draft</option>
                  </select>
                </div>
                <div class="col-md-12 mb-2">
                  <label>Short description</label>
                  {/* what the listing card shows under the title */}
                  <textarea
                    class="form-control"
                    rows={2}
                    value={form.short_description}
                    onChange={(e) =>
                      setForm({ ...form, short_description: e.target.value })
                    }
                  />
                </div>
                <div class="col-md-12 mb-2">
                  <label>Image</label>
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                  {form.id && !file && (
                    <small class="text-muted">
                      Leave empty to keep the current image.
                    </small>
                  )}
                </div>
                <div class="col-md-12 mb-2">
                  <label>Video link</label>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="YouTube link, if this blog has a video"
                    value={form.video_url}
                    onChange={(e) =>
                      setForm({ ...form, video_url: e.target.value })
                    }
                  />
                </div>
                <div class="col-md-12 mb-2">
                  <label>Content *</label>
                  <CKEditor
                    activeClass="p10"
                    content={form.description}
                    events={{
                      change: (evt) =>
                        setForm((f) => ({
                          ...f,
                          description: evt.editor.getData(),
                        })),
                      blur: (evt) =>
                        setForm((f) => ({
                          ...f,
                          description: evt.editor.getData(),
                        })),
                    }}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default BlogList;
